import './AuthenticationForm.scss';

import React from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { useFormik } from 'formik';

import TextField from '@mui/material/TextField';
import Button from 'src/component/UI/Button';
import { NavLink } from 'react-router-dom';
import yup from 'src/utils/yup';
import microsoft from '../../assets/icons/microsoft.png';
import { getIsAuthenticating } from 'src/module/authentication/selector';
import { Divider } from '@mui/material';
import { getApiAddr, useEmail, useSSO } from '../../utils/utils';
import { useTranslation } from 'src/i18n';

export default function AuthenticationForm (props) {
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: values => {
      props.handleAuthentication(values.email, values.password);
    },
    validationSchema: () => {
      return yup.object().shape({
        email: yup.string()
          .email(t('auth.invalidEmail'))
          .required(t('auth.emailRequired'))
      });
    },
  });

  const regularDomains = [
    `https://dashboard.staging.smarttrace.ai`,
    `https://my.smarttrace.ai`
  ];

  const isAuthenticating = useSelector(getIsAuthenticating);
  const [apiAddr, setApiAddr] = React.useState(process.env.REACT_APP_FOX_API_ADDR);
  const [useEmailValue, setUseEmailValue] = React.useState<boolean | null>(null);
  const [useEmailLoading, setUseEmailLoading] = React.useState(true);
  const [useSSOValue, setUseSSOValue] = React.useState<boolean | null>(null);
  const [useSSOLoading, setUseSSOLoading] = React.useState(true);

  React.useEffect(() => {
    getApiAddr().then(setApiAddr);
  }, []);

  React.useEffect(() => {
    async function loadData () {
      const emailResult = await useEmail();
      setUseEmailValue(emailResult);
      setUseEmailLoading(false);
      const ssoResult = await useSSO();
      setUseSSOValue(ssoResult);
      setUseSSOLoading(false);
    }
    loadData();
  }, []);

  return (
    <>

      {!useSSOLoading && useSSOValue && !regularDomains.find((domain) => window.location.href.startsWith(domain)) && // assume azure authentication is available for 'vanity' domains
        <>
          <div className='other-auth-options'>
            <p>{t('auth.loginWithMicrosoftAccount')}</p>

            <a className='auth-option' href={`${apiAddr}/v1/auth/azure`}>
              <img src={microsoft} height={'20px'} />
              <p>{t('auth.loginWithMicrosoft')}</p>
            </a>
          </div>
          <Divider>{t('auth.or')}</Divider>
        </>}

      <div className='main-sign-in'>
        <form className='auth-form' method='post' onSubmit={e => { e.preventDefault(); formik.handleSubmit(); }}>
          <TextField
            id='email'
            type='email'
            label={t('auth.email')}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            disabled={isAuthenticating}
            error={formik.errors.email && formik.touched.email}
            helperText={formik.touched.email && formik.errors.email}
            variant='outlined'
            fullWidth
            InputLabelProps={{ shrink: false }}
          />
          <TextField
            id='password'
            type='password'
            label={t('auth.password')}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            name='password'
            disabled={isAuthenticating}
            variant='outlined'
            fullWidth
            InputLabelProps={{ shrink: false }}
          />
          {!useEmailLoading && useEmailValue && <NavLink className='reset-link' exact to='/login/reset'>{t('auth.forgotPassword')}</NavLink>}

          <div className='auth-form-buttons'>
            <Button cta type='submit' loading={isAuthenticating}>{isAuthenticating ? t('auth.signingIn') : t('auth.signMeIn')}</Button>
          </div>

        </form>
      </div>
    </>


  );
}

AuthenticationForm.propTypes = {
  handleAuthentication: PropTypes.func.isRequired,
  redirectTo: PropTypes.string,
  isAuthenticating: PropTypes.bool
};

AuthenticationForm.defaultProps = {
};
